import { integrationApi } from "@repo/api";
import { InputField, Typography } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { useTestConnection } from "../../../hooks/useTestConnection";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { FormSection } from "../../FormSection";
import { TestConnectionBlock } from "../../TestConnectionBlock";
import { AdditionalConfigurationSection } from "../../aws/AWSIntegrationForm/AdditionalConfigurationSection";
import { getDefaultTime } from "../../aws/AWSIntegrationForm/utils";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import {
  ServiceAccountKeyUpload,
  type ServiceAccountKeyMetadata,
} from "./ServiceAccountKeyUpload";

export type { ServiceAccountKeyMetadata };

export interface GoogleVertexDiscoveryFormData {
  name: string;
  description: string;
  project_id: string;
  service_account_key: string;
  isValid: boolean;
  cftDownloaded: boolean;
}

interface GoogleVertexDiscoveryFormProps {
  integrationType: string;
  integrationId?: number;
  onFormDataChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export function GoogleVertexDiscoveryForm({
  integrationType,
  integrationId,
  onFormDataChange,
}: GoogleVertexDiscoveryFormProps) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [serviceAccountKeyContent, setServiceAccountKeyContent] = useState("");
  const [serviceAccountFileName, setServiceAccountFileName] = useState("");
  const [serviceAccountKeyMetadata, setServiceAccountKeyMetadata] =
    useState<ServiceAccountKeyMetadata | null>(null);
  const [importTime, setImportTime] = useState<Date | null>(() =>
    getDefaultTime(),
  );
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const hasPrefilledIntegrationRef = useRef(false);
  const hasPrefilledCredentialsRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;

  const { data: integrationData } = useIntegrationById(integrationId);
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: featuresResponse } =
    useIntegrationTypeFeatures("GooglePlatform");
  const featureNames = useMemo(
    () =>
      featuresResponse?.features
        ? featuresResponse.features.map((f) => f.featureName)
        : [],
    [featuresResponse?.features],
  );

  // Prefill form in edit mode: first from integrationData, then from credentials when loaded
  useEffect(() => {
    if (!integrationId || !integrationData) return;

    if (!hasPrefilledIntegrationRef.current) {
      setName(integrationData.displayName ?? "");
      setDescription((integrationData.metadata?.description as string) ?? "");
      setProjectId((integrationData.metadata?.accountId as string) ?? "");

      if (
        integrationData.features?.length &&
        integrationData.features[0].schedules?.length
      ) {
        const cronExpression =
          integrationData.features[0].schedules[0].cronExpression;
        if (cronExpression) {
          const parsed = parseCronToSchedule(cronExpression);
          setFrequency(parsed.frequency);
          if (parsed.time) {
            setImportTime(parsed.time);
          }
        }
      }
      hasPrefilledIntegrationRef.current = true;
    }

    const credsData = credentials?.data as
      | { service_account_json?: string }
      | undefined;
    const serviceAccountJson = credsData?.service_account_json;
    if (
      serviceAccountJson != null &&
      serviceAccountJson !== "" &&
      !hasPrefilledCredentialsRef.current
    ) {
      setServiceAccountKeyContent(serviceAccountJson);
      setServiceAccountFileName("service-account.json");
      try {
        const parsed = JSON.parse(serviceAccountJson) as Record<
          string,
          unknown
        >;
        setServiceAccountKeyMetadata({
          type: String(parsed.type ?? ""),
          project_id: String(parsed.project_id ?? ""),
          private_key_id: String(parsed.private_key_id ?? ""),
          client_email: String(parsed.client_email ?? ""),
          client_id: String(parsed.client_id ?? ""),
        });
      } catch {
        setServiceAccountKeyMetadata(null);
      }
      hasPrefilledCredentialsRef.current = true;
    }
  }, [integrationId, integrationData, credentials?.data]);

  const testConnection = useTestConnection(() => {
    if (!serviceAccountKeyContent.trim()) return null;
    return {
      integrationType,
      credentials: {
        type: "GCP_SERVICE_ACCOUNT",
        data: {
          service_account_json: serviceAccountKeyContent.trim(),
        },
      },
      integrationParameters: {},
    };
  });

  const isNameValid = name.trim().length > 0;
  const isProjectIdValid = projectId.trim().length > 0;
  const hasServiceKey = serviceAccountKeyContent.length > 0;
  const isValid =
    isNameValid &&
    isProjectIdValid &&
    hasServiceKey &&
    (frequency !== "24hours" || importTime !== null);
  const cftDownloaded = hasServiceKey;

  useEffect(() => {
    onFormDataChangeRef.current({
      name: name.trim(),
      description: description.trim(),
      project_id: projectId.trim(),
      service_account_key: serviceAccountKeyContent,
      frequency,
      importTime,
      isValid,
      cftDownloaded,
      featureNames,
      integrationType,
    });
  }, [
    name,
    description,
    projectId,
    serviceAccountKeyContent,
    frequency,
    importTime,
    isValid,
    cftDownloaded,
    featureNames,
    integrationType,
  ]);

  const handleServiceAccountFileSelected = (
    content: string,
    fileFileName: string,
    fileMetadata: ServiceAccountKeyMetadata | null,
  ) => {
    setServiceAccountKeyContent(content);
    setServiceAccountFileName(fileFileName);
    setServiceAccountKeyMetadata(fileMetadata);
  };

  const handleServiceAccountRemove = () => {
    setServiceAccountKeyContent("");
    setServiceAccountFileName("");
    setServiceAccountKeyMetadata(null);
  };

  return (
    <div
      className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4"
      data-testid="google-vertex-discovery-form"
    >
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType="GooglePlatform" />
          <FormSection
            icon="Connect"
            title={intl.formatMessage({
              id: "6c0sHj",
              defaultMessage:
                "What are the connection details of your integration?",
              description: "Connection details section title",
            })}
            subtitle={intl.formatMessage({
              id: "KlVUsw",
              defaultMessage:
                "Some fields have been pre-filled for you, you can update them whenever you like.",
              description: "Connection details section subtitle",
            })}
          >
            <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
              <InputField
                name="name"
                label={
                  <Typography kind="label" htmlFor="google-vertex-name">
                    <FormattedMessage
                      id="JeJjeJ"
                      defaultMessage="Integration Name"
                      description="Integration name field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={name}
                setValue={setName}
                isRequired
                isDisabled={!!integrationId}
              />

              <div>
                <Typography
                  kind="label"
                  className="block mb-1"
                  htmlFor="google-vertex-description"
                >
                  <FormattedMessage
                    id="TjUCGI"
                    defaultMessage="Integration Description"
                    description="Integration description field label"
                  />
                </Typography>
                <InputField
                  name="description"
                  placeholder=""
                  value={description}
                  setValue={setDescription}
                  kind="multiline"
                  rows={4}
                />
              </div>

              <InputField
                name="project_id"
                label={
                  <Typography kind="label" htmlFor="google-vertex-project-id">
                    <FormattedMessage
                      id="TNh5T5"
                      defaultMessage="Project ID"
                      description="Project ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={projectId}
                setValue={setProjectId}
                isRequired
              />

              <ServiceAccountKeyUpload
                content={serviceAccountKeyContent}
                fileName={serviceAccountFileName}
                metadata={serviceAccountKeyMetadata}
                onFileSelected={handleServiceAccountFileSelected}
                onRemove={handleServiceAccountRemove}
                inputId="google-vertex-service-key"
              />
              <TestConnectionBlock
                isTestingConnection={testConnection.isTestingConnection}
                connectionStatus={testConnection.connectionStatus}
                onTestConnection={testConnection.handleTestConnection}
                isDisabled={!hasServiceKey}
              />
            </div>
          </FormSection>

          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}
